<template>
  <div
    class="relative mb-16 w-full overflow-hidden md:mb-0 md:block md:w-full md:min-w-[337px] md:max-w-[498px] md:overflow-visible"
  >
    <!-- Mobile: GalleryNew-style carousel (big primary + scrollable thumbnails) -->
    <div v-if="defaultImageUrl" class="md:hidden">
      <ReviewPhotosGuidanceDrawer
        ref="mobileDrawerRef"
        :grades="gradesWithPictures"
        :initial-grade="selectedGradeLabel ?? gradesWithPictures[0]?.gradeLabel"
        name="review-photos-guidance-drawer-mobile"
        :title="i18n(translations.drawerTitle)"
      >
        <template #trigger="{ open }">
          <div class="w-[calc(100vw-48px)] max-w-[498px] overflow-hidden">
            <GalleryCarousel
              :items="mobileItems.length > 1 ? mobileItems : [mobileItems[0]]"
              primary-image-wrapper-classes="pl-24"
              :product-name="trackingModel"
              :secondary-list-classes="`${CAROUSEL_MAX_HEIGHT_CLASS} ![grid-template-rows:repeat(2,auto)] !pr-24`"
            >
              <template #first="{ items }">
                <div class="relative h-full w-[275px]">
                  <RevIllustration
                    v-if="items[0]?.src"
                    alt=""
                    class="rounded-lg h-full w-full object-cover"
                    :height="498"
                    :src="items[0].src"
                    :width="498"
                  />
                  <div class="absolute inset-x-0 bottom-0 p-8">
                    <RevTag
                      class="bg-overlay-low text-static-light-hi"
                      :label="exampleTag"
                      variant="secondary"
                    />
                  </div>
                </div>
              </template>
              <template #rest="{ items }">
                <template v-if="isPendingReviewsPerGrade">
                  <li
                    v-for="n in 4"
                    :key="`skeleton-${n}`"
                    class="shrink-0 [grid-row:span_1] [grid-column:span_1]"
                    :class="SMALL_ALT_ITEM_SIZE_CLASS"
                  >
                    <RevSkeleton
                      height="130px"
                      shape="rectangle"
                      width="130px"
                    />
                  </li>
                  <li aria-hidden="true" class="w-[80px] shrink-0" />
                </template>
                <template v-else>
                  <li
                    v-for="(item, index) in items"
                    v-show="index !== 0"
                    :key="item.id || item.src"
                    class="shrink-0 cursor-pointer [grid-row:span_1] [grid-column:span_1] md:[grid-row:initial] md:[grid-column:initial]"
                    :class="SMALL_ALT_ITEM_SIZE_CLASS"
                    role="button"
                    tabindex="0"
                    @click="handleMobileReviewClick(open, index)"
                    @keydown.enter.prevent="
                      handleMobileReviewClick(open, index)
                    "
                  >
                    <div
                      class="rounded-lg relative h-full w-full overflow-hidden"
                      data-test="review-photos-guidance-slide"
                    >
                      <RevIllustration
                        :alt="item.alt || ''"
                        class="rounded-lg h-full w-full object-cover"
                        :height="130"
                        :src="item.src"
                        :width="130"
                      />
                      <div
                        class="rounded-lg absolute inset-0 flex flex-col justify-between p-8"
                      >
                        <RevTag
                          v-if="item.customerName"
                          class="self-start"
                          :label="item.customerName"
                          variant="secondary"
                        />
                        <span
                          v-if="item.reviewDate"
                          class="text-static-light-hi body-1-bold [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
                        >
                          {{ item.reviewDate }}
                        </span>
                      </div>
                    </div>
                  </li>
                  <li aria-hidden="true" class="w-[80px] shrink-0 md:hidden" />
                </template>
              </template>
            </GalleryCarousel>
          </div>
        </template>
      </ReviewPhotosGuidanceDrawer>
    </div>

    <!-- Desktop: default image + RevCardCarousel -->
    <div v-if="defaultImageUrl" class="hidden md:block">
      <div
        class="rounded-lg relative flex w-full aspect-square overflow-hidden md:rounded-[32px]"
        data-test="review-photos-guidance-default-image"
      >
        <RevIllustration
          alt=""
          class="rounded-lg h-full w-full object-cover md:rounded-[32px]"
          :height="498"
          :src="defaultImageUrl"
          :width="498"
        />
        <div class="absolute inset-x-0 bottom-0 p-16">
          <RevTag
            class="bg-overlay-low text-static-light-hi"
            :label="exampleTag"
            variant="secondary"
          />
        </div>
      </div>
    </div>

    <div
      v-else-if="isPendingReviewsPerGrade"
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <RevSpinner size="large" />
    </div>

    <div
      v-if="hasReviewsWithAttachments || isPendingReviewsPerGrade"
      class="bg-surface-default-hi mt-16 hidden rounded-lg p-16 md:rounded-[32px] md:block md:p-24"
    >
      <template v-if="isPendingReviewsPerGrade">
        <RevSkeleton
          class="mb-16"
          height="24px"
          shape="rectangle"
          width="220px"
        />
        <div class="flex gap-8">
          <RevSkeleton
            v-for="n in 4"
            :key="n"
            height="130px"
            shape="rectangle"
            width="130px"
          />
        </div>
      </template>

      <ReviewPhotosGuidanceDrawer
        v-else
        ref="drawerRef"
        :grades="gradesWithPictures"
        :initial-grade="selectedGradeLabel ?? gradesWithPictures[0]?.gradeLabel"
        :title="i18n(translations.drawerTitle)"
      >
        <template #trigger="{ open }">
          <RevCardCarousel
            :center="false"
            desktop-card-width="narrow"
            mobile-card-width="narrow"
            :next-alternative-text="i18n(translations.alternativeNext)"
            :prev-alternative-text="i18n(translations.alternativePrevious)"
            size="extra-small"
            :subtitle="i18n(translations.subtitle)"
            :title="carouselTitle"
          >
            <div
              v-for="(slide, position) in reviewSlides"
              :key="slide.id"
              class="h-full"
            >
              <RevCard
                class="h-full cursor-pointer overflow-hidden hover:drop-shadow-xl"
                @click="handleReviewCardClick(open, { slide, position })"
              >
                <div
                  class="rounded-lg relative flex w-full aspect-square overflow-hidden"
                  data-test="review-photos-guidance-slide"
                >
                  <RevIllustration
                    :alt="slide.title"
                    class="rounded-lg h-full w-full object-cover"
                    :height="130"
                    :src="slide.imageUrl"
                    :width="130"
                  />

                  <div
                    class="rounded-lg absolute inset-0 flex flex-col justify-between p-8"
                  >
                    <RevTag
                      v-if="slide.customerName"
                      class="self-start"
                      :label="slide.customerName"
                      variant="secondary"
                    />
                    <span
                      v-if="slide.reviewDate"
                      class="text-static-light-hi body-2-bold [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
                    >
                      {{ slide.reviewDate }}
                    </span>
                  </div>
                </div>
              </RevCard>
            </div>
          </RevCardCarousel>
        </template>
      </ReviewPhotosGuidanceDrawer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevCard } from '@ds/components/Card'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag } from '@ds/components/Tag'

import {
  CAROUSEL_MAX_HEIGHT_CLASS,
  SMALL_ALT_ITEM_SIZE_CLASS,
} from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.constants'
import type { GalleryImage } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import GalleryCarousel from '~/scopes/product-core/components/presentationals/GalleryNew/components/Carousel/Carousel.vue'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  BACKBOX_GRADE_LABELS,
  type BackboxGradeLabel,
  GRADE_DEFAULT_IMAGE,
  GRADE_LABEL_BY_VALUE,
} from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.constants'
import type { ReviewsPerGrade } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.types'
import type { DisplayedReviewWithAttachments } from '~/scopes/reviews/reviews-display/models/reviews'

import translations from './ReviewPhotosGuidance.translations'
import type {
  FormattedReview,
  GradePictures,
  PictureItem,
  ReviewSlide,
} from './ReviewPhotosGuidanceDrawer.types'
import ReviewPhotosGuidanceDrawer from './ReviewPhotosGuidanceDrawer.vue'

const props = withDefaults(
  defineProps<{
    trackingModel?: string
    reviewsPerGrade?: ReviewsPerGrade | null
    isPendingReviewsPerGrade?: boolean
  }>(),
  {
    trackingModel: '',
    reviewsPerGrade: null,
    isPendingReviewsPerGrade: false,
  },
)

const route = useRoute()
const { grade } = useUrlParams()
const i18n = useI18n()
const locale = useI18nLocale()
const { trackClick } = useTracking()

const drawerRef =
  useTemplateRef<InstanceType<typeof ReviewPhotosGuidanceDrawer>>('drawerRef')

const mobileDrawerRef =
  useTemplateRef<InstanceType<typeof ReviewPhotosGuidanceDrawer>>(
    'mobileDrawerRef',
  )

const selectedGradeLabel = computed<BackboxGradeLabel | undefined>(() => {
  if (grade.value === undefined || grade.value === null) return undefined

  return GRADE_LABEL_BY_VALUE[grade.value]
})

const defaultImageUrl = computed<string | null>(() => {
  if (!selectedGradeLabel.value) return null

  return GRADE_DEFAULT_IMAGE[selectedGradeLabel.value]
})

const GRADE_DISPLAY: Record<
  BackboxGradeLabel,
  {
    title: typeof translations.titlePremium
    exampleTag: typeof translations.exampleTagPremium
  }
> = {
  PREMIUM: {
    title: translations.titlePremium,
    exampleTag: translations.exampleTagPremium,
  },
  FLAWLESS: {
    title: translations.titleFlawless,
    exampleTag: translations.exampleTagFlawless,
  },
  VERY_GOOD: {
    title: translations.titleVeryGood,
    exampleTag: translations.exampleTagVeryGood,
  },
  ECO: {
    title: translations.titleEco,
    exampleTag: translations.exampleTagEco,
  },
}

const gradeDisplay = computed(() =>
  selectedGradeLabel.value ? GRADE_DISPLAY[selectedGradeLabel.value] : null,
)

const carouselTitle = computed(() =>
  gradeDisplay.value ? i18n(gradeDisplay.value.title) : '',
)

const exampleTag = computed(() =>
  gradeDisplay.value ? i18n(gradeDisplay.value.exampleTag) : '',
)

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    month: 'short',
    year: 'numeric',
  })
}

function toFormattedReview(
  review: DisplayedReviewWithAttachments,
): FormattedReview {
  return {
    ...review,
    reviewDate: review.createdAt,
    purchaseDate: review.product.purchasedAt,
    pictures: review.attachments.map(({ url }) => url),
  }
}

/**
 * All grade labels, each with a flat list of pictures. Every grade starts
 * with the default grade image as the first slide (index 0), followed by
 * review photos. The default image slide has `review: null` so the right
 * side can show a default content card instead of a review.
 */
const gradesWithPictures = computed<GradePictures[]>(() => {
  if (!props.reviewsPerGrade) return []

  return BACKBOX_GRADE_LABELS.flatMap((gradeLabel) => {
    const defaultImage = GRADE_DEFAULT_IMAGE[gradeLabel]
    if (!defaultImage) return []

    const defaultPicture: PictureItem = {
      id: `default-${gradeLabel}`,
      url: defaultImage,
      review: null,
    }

    const reviews = props.reviewsPerGrade?.[gradeLabel] ?? []

    const reviewPictures: PictureItem[] = reviews.flatMap((review) => {
      if (!review.attachments || review.attachments.length === 0) return []

      const formatted = toFormattedReview(
        review as DisplayedReviewWithAttachments,
      )

      return review.attachments.map((attachment, index) => ({
        id: `${review.id}-${index}`,
        url: attachment.url,
        review: formatted,
      }))
    })

    return [{ gradeLabel, pictures: [defaultPicture, ...reviewPictures] }]
  })
})

const hasReviewsWithAttachments = computed(
  () => gradesWithPictures.value.length > 0,
)

const reviewSlides = computed<ReviewSlide[]>(() => {
  return gradesWithPictures.value.flatMap(({ gradeLabel, pictures }) => {
    return pictures
      .map((picture, index) => ({ picture, index }))
      .filter(({ picture }) => picture.review !== null)
      .map(({ picture, index }) => {
        const review = picture.review as FormattedReview
        const firstName = review.customer?.firstName ?? null
        const lastName = review.customer?.lastName ?? null
        const customerName =
          firstName || lastName
            ? [firstName, lastName].filter(Boolean).join(' ')
            : null

        return {
          id: picture.id,
          imageUrl: picture.url,
          title: review.product.title,
          customerName,
          reviewDate: review.createdAt ? formatDate(review.createdAt) : null,
          gradeLabel,
          pictureIndex: index,
        }
      })
  })
})

function handleReviewCardClick(
  openModal: () => void,
  { slide, position }: { slide: ReviewSlide; position: number },
) {
  drawerRef.value?.handleReviewCardClick(slide.gradeLabel, slide.pictureIndex)
  openModal()

  trackClick(
    removeEmptyValuesInObject({
      page_type: route.name as string,
      position,
      product_model: props.trackingModel || slide.imageUrl,
      zone: 'review_photo_guidance',
    }),
  )
}

function handleMobileReviewClick(openModal: () => void, index: number) {
  const slide = reviewSlides.value[index - 1]
  if (!slide) return

  mobileDrawerRef.value?.handleReviewCardClick(
    slide.gradeLabel,
    slide.pictureIndex,
  )
  openModal()

  trackClick(
    removeEmptyValuesInObject({
      page_type: route.name as string,
      position: index,
      product_model: props.trackingModel || slide.imageUrl,
      zone: 'review_photo_guidance',
    }),
  )
}

const mobileItems = computed<
  (GalleryImage & {
    customerName?: string | null
    reviewDate?: string | null
  })[]
>(() => {
  if (!defaultImageUrl.value) return []

  const items: (GalleryImage & {
    customerName?: string | null
    reviewDate?: string | null
  })[] = [
    {
      id: 'default-grade-image',
      src: defaultImageUrl.value,
      alt: '',
      customerName: null,
      reviewDate: null,
    },
  ]

  return [
    ...items,
    ...reviewSlides.value.map((slide) => ({
      id: slide.id,
      src: slide.imageUrl,
      alt: slide.title,
      customerName: slide.customerName,
      reviewDate: slide.reviewDate,
    })),
  ]
})
</script>
